import { AbdmFhirBundleIndex } from './abdm-fhir-bundle-index';
import { FhirObject, asObject, isObject, readObjects, readString } from './abdm-fhir-readers';

export interface AbdmAttachment {
  readonly title: string;
  readonly contentType: string;
  readonly data: string;
}

// Only these open in the browser. Anything else from another system (HTML, SVG) could run script.
const OPENABLE_CONTENT_TYPES: readonly string[] = ['application/pdf', 'image/jpeg', 'image/png'];
const DEFAULT_TITLE = 'Attachment';

export function readAttachments(index: AbdmFhirBundleIndex): AbdmAttachment[] {
  return [
    ...index.ofTypes(['DocumentReference']).flatMap(readDocumentAttachments),
    ...index.ofTypes(['DiagnosticReport']).flatMap(report => readObjects(report, 'presentedForm')),
    ...index.ofTypes(['Binary'])
  ].map(toAttachment).filter((attachment): attachment is AbdmAttachment => attachment !== undefined);
}

export function toAttachmentBlob(attachment: AbdmAttachment): Blob {
  const binary = atob(attachment.data.replace(/\s/g, ''));
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
  return new Blob([bytes], { type: attachment.contentType });
}

function readDocumentAttachments(document: FhirObject): FhirObject[] {
  return readObjects(document, 'content').map(content => asObject(content['attachment'])).filter(isObject);
}

// A FHIR Attachment and a Binary resource both carry contentType and base64 data.
function toAttachment(source: FhirObject): AbdmAttachment | undefined {
  const contentType = (readString(source, 'contentType') || '').split(';')[0].toLowerCase();
  const data = readString(source, 'data');
  if (!data || !OPENABLE_CONTENT_TYPES.includes(contentType)) {
    return undefined;
  }
  return { title: readString(source, 'title') || DEFAULT_TITLE, contentType, data };
}
